import * as React from 'react';
import { PurchasedItem } from '~/components/Purchased/Purchased';
import { OrangeButton } from './Buttons';
import { t } from '~/utils/localization';
import { settings } from '~/utils/settings';
import { DivTooltip } from '@wg/wows-react-uikit';
import { useDispatch, useSelector } from 'react-redux';
import equal from 'fast-deep-equal';
import WSMartPurchaseProcessor from '~/processors/WSMartPurchaseProcessor';
import DefaultTooltip from '~/components/Tooltip/DefaultTooltip';
import { ICurrentPage } from '~/Actions/ActionAppType';
import { State } from '~/Reducers';
import { DWH_EVENTS as WOWS_COMMERCE_DWH_EVENTS } from '@wg/wows-commerce/constants/dwhEvents';
import { RegionalInfo } from '@wg/wows-commerce/api/user';
import { changeVisiblePopup } from '~/Actions/ActionApp';
import { POPUPS_NAME } from '~/components/PopupManager';
import { isFreeBundle } from '~/utils/bundles';

interface IRealPurchaseButton {
    bundle: IBundle;
    quantity?: number;
    buttonClassName?: string | string[];
    onMouseMove?: (event: React.MouseEvent) => void;
    isDisabled?: boolean;
    onClickFromDisableButton?: (event: React.MouseEvent) => void;
}

interface IStateSelector {
    currentPage: ICurrentPage;
    regional: Nullable<RegionalInfo>;
}

const stateSelector = (state: State): IStateSelector => {
    return {
        currentPage: state.ReducerApp.currentPage,
        regional: state.ReducerWSMart.regional,
    };
};

const RealPurchaseButton = ({ bundle, buttonClassName, onMouseMove, onClickFromDisableButton, isDisabled, quantity }: IRealPurchaseButton) => {
    const dispatch = useDispatch();
    const state = useSelector<State, IStateSelector>(stateSelector, equal);

    if (bundle.isPurchased) {
        return <PurchasedItem bundle={bundle} />;
    }

    if (bundle.isLoading) {
        return <OrangeButton label={t('Купить')} disabled />;
    }

    if (bundle.originalProduct?.isVariablePriceProduct && !state.currentPage?.isBundlePage) {
        return <OrangeButton label={t('Перейти')} />;
    }

    const isFree = isFreeBundle(bundle);

    if (settings.disableRealPurchaseForTesting && !isFree) {
        return (
            <DivTooltip tooltipBody={<DefaultTooltip text={t('Покупки недоступны на этапе тестирования')} />}>
                <OrangeButton label={t('Купить')} disabled={true} />
            </DivTooltip>
        );
    }

    const dwhEventName = state.currentPage?.isBundlePage ? WOWS_COMMERCE_DWH_EVENTS.OPEN_PURCHASE_POPUP_FROM_BUNDLE : WOWS_COMMERCE_DWH_EVENTS.OPEN_PURCHASE_POPUP_FROM_CATEGORY;

    return (
        <OrangeButton
            label={isFree ? t('Получить') : t('Купить')}
            isFree={isFree}
            onMouseMove={onMouseMove}
            onFallback={onClickFromDisableButton}
            onClick={(event) => {
                if (!bundle.originalProduct?.isPurchasable) {
                    return;
                }
                event.stopPropagation();
                if (state.regional && !state.regional.purchaseIsAllowed) {
                    dispatch(changeVisiblePopup(POPUPS_NAME.FORBIDDEN_REGION, true, state.regional));
                    return;
                }
                const wsmartProcessor = new WSMartPurchaseProcessor(bundle.originalProduct);
                wsmartProcessor.setBundle(bundle);
                wsmartProcessor.buy(dwhEventName, quantity || 1);
            }}
            disabled={isDisabled || !bundle.originalProduct?.isPurchasable}
            className={buttonClassName}
        />
    );
};

export default RealPurchaseButton;
